import { isHeroBrandEnabled } from './heroBrandUtils';

const isContractActive = (contract) => {
  if (!contract) return false;

  if (contract.active === false || contract.isActive === false) {
    return false;
  }

  if (typeof contract.status === 'string' && contract.status.toLowerCase() !== 'active') {
    return false;
  }

  return true;
};

const normalizeContract = (contract) => {
  const id = contract?.id || contract?._id || contract?.contractId || contract?.ContractID;
  const name = contract?.name || contract?.contractName || contract?.title || contract?.Name;

  if (!id) {
    return null;
  }

  return {
    id,
    name: name || String(id),
    // TUL-4996: target (customer) languages this contract allows. Empty => no restriction.
    targetLanguageIds: Array.isArray(contract?.targetLanguageIds) ? contract.targetLanguageIds : [],
    raw: contract,
  };
};

export const getActiveContractsFromUser = (user) => {
  const candidateContracts = user?.Customer?.Contracts
    || user?.customer?.contracts
    || user?.contracts
    || [user?.contract];

  const normalized = candidateContracts
    .filter(isContractActive)
    .map(normalizeContract)
    .filter(Boolean);

  const seen = new Set();
  return normalized.filter((contract) => {
    if (seen.has(contract.id)) {
      return false;
    }
    seen.add(contract.id);
    return true;
  });
};

export const getContractById = (contracts, contractId) => {
  if (!contractId) return null;
  return contracts.find((contract) => String(contract.id) === String(contractId)) || null;
};

// TUL-4996: a contract is orderable only when it allows at least one of the customer's enabled
// target languages. Empty/absent targetLanguageIds means no restriction => always orderable.
// Matching is by languageId (actual language id), mirroring applyContractTargetLanguages.
export const contractAllowsAnyTargetLanguage = (contract, targetLanguages = []) => {
  const allowed = contract?.targetLanguageIds;
  if (!Array.isArray(allowed) || allowed.length === 0) {
    return true;
  }
  return (targetLanguages || []).some((tl) => allowed.includes(tl?.languageId));
};

// TUL-4996: active contracts narrowed to those orderable for the customer's target languages.
// Only herotolk is gated; every other brand keeps the full active list unchanged.
export const getOrderableContractsFromUser = (user) => {
  const active = getActiveContractsFromUser(user);
  if (!isHeroBrandEnabled()) {
    return active;
  }
  return active.filter((contract) => contractAllowsAnyTargetLanguage(contract, user?.targetLanguages));
};

// TUL-5355: a contract only restricts modalities when it defines interpretation types (kept on the
// raw contract, mirroring the presence filtering in PrebookingDetailsStep). Empty/absent
// interpretationTypes means no restriction => translation allowed.
export const contractAllowsTranslation = (contract) => {
  const interpretationTypes = contract?.raw?.interpretationTypes;
  if (!Array.isArray(interpretationTypes) || interpretationTypes.length === 0) {
    return true;
  }
  return interpretationTypes.includes('prebooking_translation');
};

// TUL-5355: orderable contracts narrowed to those that allow translation orders. Applies to every
// brand -- the capability comes from the contract's own interpretation types.
export const getTranslationOrderableContractsFromUser = (user) =>
  getOrderableContractsFromUser(user).filter(contractAllowsTranslation);

// TUL-5355: whether any active contract allows translations, before the hero target-language
// narrowing -- distinguishes a genuine capability gap from a language mismatch when blocking.
export const hasTranslationCapableContract = (user) =>
  getActiveContractsFromUser(user).some(contractAllowsTranslation);

// TUL-5355: block translation ordering when the customer has active contracts but none of them is
// usable for a translation order (capability, plus the hero target-language narrowing). A customer
// with zero contracts keeps the existing (null-contract) flow.
export const shouldBlockTranslationOrdering = (user) => {
  if (getActiveContractsFromUser(user).length === 0) {
    return false;
  }
  return getTranslationOrderableContractsFromUser(user).length === 0;
};

// TUL-4996: block ordering when a herotolk customer has active contracts but none are orderable
// for their target languages. A customer with zero contracts keeps the existing (null-contract) flow.
export const shouldBlockOrdering = (user) => {
  if (!isHeroBrandEnabled()) {
    return false;
  }
  if (getActiveContractsFromUser(user).length === 0) {
    return false;
  }
  return getOrderableContractsFromUser(user).length === 0;
};
